import type { EngineLine, EngineScore } from '../../types';

/**
 * Parsing du protocole UCI émis par Stockfish. Isolé pour être testable
 * unitairement sans instancier de vrai Worker.
 */

/** Parse une ligne "info depth ... score cp|mate ... pv ..." en EngineLine, ou null si non pertinente. */
export function parseInfoLine(line: string): EngineLine | null {
  if (!line.startsWith('info')) return null;
  if (!line.includes(' pv ')) return null; // ignore les lignes "info" sans variante (ex: currmove)

  const depthMatch = line.match(/\bdepth (\d+)/);
  const scoreMatch = line.match(/\bscore (cp|mate) (-?\d+)/);
  const pvMatch = line.match(/\bpv (.+)$/);

  if (!depthMatch || !scoreMatch || !pvMatch) return null;

  const depth = Number(depthMatch[1]);
  const scoreType = scoreMatch[1] as 'cp' | 'mate';
  const scoreValue = Number(scoreMatch[2]);
  const score: EngineScore =
    scoreType === 'cp' ? { type: 'cp', value: scoreValue } : { type: 'mate', value: scoreValue };
  const pvUci = pvMatch[1].trim().split(/\s+/);

  return { score, pvUci, depth };
}

/** Parse une ligne "bestmove <uci> [ponder <uci>]". Retourne null si mat/pat (bestmove (none)). */
export function parseBestMove(line: string): string | null {
  if (!line.startsWith('bestmove')) return null;
  const match = line.match(/^bestmove (\S+)/);
  if (!match) return null;
  if (match[1] === '(none)') return null;
  return match[1];
}

/**
 * Convertit un score renvoyé par Stockfish (point de vue du camp au trait
 * dans la position analysée) en score normalisé du point de vue des Blancs,
 * convention utilisée dans tout le reste de l'application.
 */
export function normalizeScoreToWhitePov(score: EngineScore, sideToMove: 'w' | 'b'): EngineScore {
  if (sideToMove === 'w') return score;
  return score.type === 'cp' ? { type: 'cp', value: -score.value } : { type: 'mate', value: -score.value };
}

/** Convertit un score "point de vue Blancs" en score "point de vue du joueur donné". */
export function scoreFromPlayerPov(score: EngineScore, playerColor: 'w' | 'b'): EngineScore {
  if (playerColor === 'w') return score;
  return score.type === 'cp' ? { type: 'cp', value: -score.value } : { type: 'mate', value: -score.value };
}

/** Valeur numérique comparable d'un score (mat traité comme un centipion extrême, borné). */
export function scoreToComparable(score: EngineScore): number {
  if (score.type === 'cp') return score.value;
  // Un mat en N coups vaut plus qu'importe quel avantage matériel ; le signe
  // porte le camp gagnant, la magnitude décroît avec la distance au mat pour
  // qu'un "mate in 1" reste mieux qu'un "mate in 5" pour le même camp.
  const MATE_BASE = 100000;
  return score.value > 0 ? MATE_BASE - score.value : -MATE_BASE - score.value;
}
